import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { allVenues } from "../../../mocks/allVenues";
import type { VenueDetail } from "../../../mocks/allVenues";
import VenueFormModal from "../components/VenueFormModal";
import ConfirmModal from "../components/ConfirmModal";

type SortKey = "name" | "rating" | "reviews" | "district";

const CATEGORY_COLORS: Record<string, string> = {
  "Tarihi Yerler":    "bg-amber-100 text-amber-700",
  "Lezzet Durakları": "bg-red-100 text-red-700",
  "Eğlence":          "bg-sky-100 text-sky-700",
  "Konaklama":        "bg-violet-100 text-violet-700",
  "Doğa ve Rotalar":  "bg-emerald-100 text-emerald-700",
  "Gezilecek Yerler": "bg-stone-100 text-stone-600",
};

export default function VenuesSection() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<VenueDetail[]>(allVenues);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("name");

  type ModalState =
    | { type: "none" }
    | { type: "add" }
    | { type: "edit"; venue: VenueDetail }
    | { type: "delete"; venue: VenueDetail };

  const [modal, setModal] = useState<ModalState>({ type: "none" });

  const categories = useMemo(() => Array.from(new Set(allVenues.map((v) => v.category))), []);

  const filtered = useMemo(() => {
    let result = [...venues];
    if (search) result = result.filter((v) =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      v.district.toLowerCase().includes(search.toLowerCase())
    );
    if (categoryFilter !== "all") result = result.filter((v) => v.category === categoryFilter);
    result.sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name, "tr");
      if (sortKey === "rating") return b.rating - a.rating;
      if (sortKey === "reviews") return b.reviews - a.reviews;
      if (sortKey === "district") return a.district.localeCompare(b.district, "tr");
      return 0;
    });
    return result;
  }, [venues, search, categoryFilter, sortKey]);

  const toggleSponsored = (id: string) =>
    setVenues((prev) => prev.map((v) => v.id === id ? { ...v, isSponsored: !v.isSponsored } : v));
  const toggleEditorPick = (id: string) =>
    setVenues((prev) => prev.map((v) => v.id === id ? { ...v, isEditorPick: !v.isEditorPick } : v));
  const toggleOpen = (id: string) =>
    setVenues((prev) => prev.map((v) => v.id === id ? { ...v, isOpen: !v.isOpen } : v));

  const handleAdd = (data: Partial<VenueDetail>) => {
    const newVenue: VenueDetail = {
      id: `venue-${Date.now()}`,
      name: data.name ?? "",
      category: data.category ?? "",
      categorySlug: data.categorySlug ?? "",
      rating: data.rating ?? 4.0,
      reviews: data.reviews ?? 0,
      distanceKm: data.distanceKm ?? 1.0,
      image: data.image ?? "https://public.readdy.ai/ai/img_res/2b086e38d872ce7015e74641920f900e.jpg",
      address: data.address ?? "",
      district: data.district ?? "",
      isSponsored: data.isSponsored ?? false,
      isEditorPick: data.isEditorPick ?? false,
      isOpen: data.isOpen ?? true,
      description: data.description ?? "",
      phone: data.phone ?? "",
      tags: data.tags ?? [],
      createdOrder: venues.length + 1,
    };
    setVenues((prev) => [newVenue, ...prev]);
    setModal({ type: "none" });
  };

  const handleEdit = (data: Partial<VenueDetail>) => {
    if (modal.type !== "edit") return;
    setVenues((prev) => prev.map((v) =>
      v.id === modal.venue.id ? { ...v, ...data, id: v.id, createdOrder: v.createdOrder } : v
    ));
    setModal({ type: "none" });
  };

  const handleDelete = () => {
    if (modal.type !== "delete") return;
    setVenues((prev) => prev.filter((v) => v.id !== modal.venue.id));
    setModal({ type: "none" });
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-4 border border-stone-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Mekan veya ilçe ara..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-emerald-400 transition-colors" />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none text-stone-700 cursor-pointer">
          <option value="all">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none text-stone-700 cursor-pointer">
          <option value="name">İsme Göre</option>
          <option value="rating">Puana Göre</option>
          <option value="reviews">Yoruma Göre</option>
          <option value="district">İlçeye Göre</option>
        </select>
        <span className="text-xs text-stone-400 ml-auto">{filtered.length} mekan</span>
        <button onClick={() => setModal({ type: "add" })}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Mekan Ekle
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Mekan</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Kategori</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">İlçe</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Puan</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Durum</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Sponsorlu</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Editör</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((venue) => (
                <tr key={venue.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={venue.image} alt={venue.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-stone-800 truncate max-w-[160px]">{venue.name}</p>
                        <p className="text-xs text-stone-400 truncate max-w-[160px]">{venue.address}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${CATEGORY_COLORS[venue.category] ?? "bg-stone-100 text-stone-600"}`}>
                      {venue.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-600 text-xs">{venue.district}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <i className="ri-star-fill text-amber-500 text-xs" />
                      <span className="font-semibold text-stone-700">{venue.rating}</span>
                    </div>
                    <div className="text-xs text-stone-400">{venue.reviews.toLocaleString()} yorum</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleOpen(venue.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        venue.isOpen ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-600 hover:bg-red-200"
                      }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${venue.isOpen ? "bg-emerald-500" : "bg-red-400"}`} />
                      {venue.isOpen ? "Açık" : "Kapalı"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleSponsored(venue.id)}
                      className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${venue.isSponsored ? "bg-amber-500" : "bg-stone-200"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${venue.isSponsored ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleEditorPick(venue.id)}
                      className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${venue.isEditorPick ? "bg-emerald-600" : "bg-stone-200"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${venue.isEditorPick ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => navigate(`/venue/${venue.id}`)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors cursor-pointer" title="Görüntüle">
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <button onClick={() => setModal({ type: "edit", venue })}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer" title="Düzenle">
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button onClick={() => setModal({ type: "delete", venue })}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer" title="Sil">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-16 text-center text-stone-400">
            <i className="ri-search-line text-3xl block mb-2" />
            <p className="text-sm">Sonuç bulunamadı</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal.type === "add" && (
        <VenueFormModal mode="add" onSave={handleAdd} onClose={() => setModal({ type: "none" })} />
      )}
      {modal.type === "edit" && (
        <VenueFormModal mode="edit" initial={modal.venue} onSave={handleEdit} onClose={() => setModal({ type: "none" })} />
      )}
      {modal.type === "delete" && (
        <ConfirmModal
          title="Mekanı Sil"
          message={`"${modal.venue.name}" adlı mekanı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
          confirmLabel="Evet, Sil"
          onConfirm={handleDelete}
          onCancel={() => setModal({ type: "none" })}
        />
      )}
    </div>
  );
}
